/**
 * keyOverrides — the passes a person put on, or took off, a payroll run.
 *
 * Mikko, 2026-09-15: "the WIP can be remove or add … when i started to
 * simulate or testing it to HR/USER she will cleanse first the Paid WIP or
 * not yet Paid WIP so removing or adding WIP is very important here."
 *
 * The run derives what it pays from BQC5 and a date window, and that is right
 * almost always. The exceptions are the whole job: a bundle the sewer says
 * was not hers, a delivery QC'd the morning after the cut-off that she is
 * owed for this week, a row being set aside while somebody checks it. Until
 * now the only lever was the window, which moves everybody at once.
 *
 * ONE MAP, TWO DIRECTIONS, because they are the same decision. A person is
 * saying "this particular pass does / does not belong on this payslip", and
 * storing that as two lists would let a key end up in both.
 *
 *   held      priced by the run, but taken off by hand.
 *   included  outside the run's window, put on by hand.
 *
 * KEYED ON THE PASS, NOT THE WIP. The run's whole ledger — `scanKeys` on a
 * payout line, `paidKeys` from the cursor — is event keys (`qc:<spId>`,
 * `wb:<date>:<wip>:<qty>:<serial>`), and a WIP inspected twice is two passes
 * that can be worth different money. Keying on the WIP would take both off
 * when somebody meant one. The screen still SHOWS a WIP and its SKU, because
 * that is what a person recognises.
 *
 * AN ALREADY-PAID PASS CANNOT BE INCLUDED, and that guard does not live here
 * — it lives in `buildDraftRun`, which checks the paid set before it looks at
 * any of this. An override is a request; the run decides. That ordering is
 * what stops a second payslip for work somebody has already been paid for,
 * and it is pinned by a test in payoutDraft.test.ts.
 *
 * Stored as one JSON blob in `iSyn2_SubconPayrollSettings`, the same way the
 * pants choices, the sewer aliases and the WIP plans are. Pure and I/O-free.
 */

/** The settings row this map lives in. */
export const KEY_OVERRIDES_KEY = "keyOverrides";

/** What a person did to one pass. */
export type KeyOverride = "held" | "included";

/** Event key → what was done to it. */
export type StoredKeyOverrides = Record<string, KeyOverride>;

const VALID = new Set<string>(["held", "included"]);

/** An override from anything, or null — so a hand-edited settings row cannot
 *  put a word the run does not understand into the derivation. */
export function asKeyOverride(v: unknown): KeyOverride | null {
  const s = String(v ?? "").trim().toLowerCase();
  return VALID.has(s) ? (s as KeyOverride) : null;
}

/**
 * Parse the stored blob, keeping only what is usable.
 *
 * Never throws. A settings row that will not parse means nobody has held or
 * added anything, which is the state the run was in before this existed —
 * the safe direction, because it derives exactly what BQC5 says.
 */
export function parseStoredKeyOverrides(text: string | null | undefined): StoredKeyOverrides {
  if (!text) return {};
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    return {};
  }
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const out: StoredKeyOverrides = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    const key = String(k ?? "").trim();
    const o = asKeyOverride(v);
    if (key && o) out[key] = o;
  }
  return out;
}

/** True when this pass was taken off the run by hand. */
export const isHeld = (overrides: StoredKeyOverrides | undefined, key: string): boolean =>
  overrides?.[key] === "held";

/** True when this pass was put on the run by hand, from outside the window. */
export const isIncluded = (overrides: StoredKeyOverrides | undefined, key: string): boolean =>
  overrides?.[key] === "included";

/** How many passes a person has touched, for the screen to say so out loud. */
export function countOverrides(overrides: StoredKeyOverrides | undefined): { held: number; included: number } {
  let held = 0, included = 0;
  for (const v of Object.values(overrides ?? {})) {
    if (v === "held") held++;
    else if (v === "included") included++;
  }
  return { held, included };
}
